import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import lodashGet from "lodash/get";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import { PageFooter, SaveButton, FileInput, FileField, SimpleForm, showNotification, required } from "jazasoft";

// local
import ExcelErrorDialog from "../../../components/ExcelErrorDialog";
import useSafeState from "../../../hooks/useSafeState";
import useMutation from "../../../hooks/useMutation";
import { isEmpty } from "../../../utils/helpers";
import { REGEX_POSITIVE_DECIMAL } from "../../../utils/regex";

const rollPredefinedFields = ["supplierRollNo", "factoryRollNo", "length", "weight", "width", "gsm", "rackNo", "blanketQty"];

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3),
    marginTop: "0.5em"
  },
  button: {
    margin: "0 1em"
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "&>*": {
      marginRight: "1em"
    }
  },
  drawerHeader: {
    display: "flex",
    width: 800,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const FormFooter = props => (
  <PageFooter>
    <SaveButton label="Upload" {...props} />
  </PageFooter>
);

const BlanketIssueDrawer = props => {
  const classes = useStyles();
  const { open, invoice, fabricItem, onDrawerClose, onRefresh } = props;

  const dispatch = useDispatch();
  const i18nMessages = useSelector(state => state?.i18n?.messages);

  const [errorList, setErrorList] = useSafeState([]);

  const [mutate] = useMutation();

  //** Supplier Rolls Upload against Fabric Item */
  const onUpload = values => {
    const { file } = values;
    var data = new FormData();
    data.append("file", file && file.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];

          const headerRow = excelRowList[0];
          const headerMap = headerRow.cellList.reduce((acc, c) => ({ ...acc, [`${c.colNo}`]: c.value || "" }), {});

          const rollList = excelRowList
            .slice(1)
            .map(row => row.cellList.reduce((acc, c) => ({ ...acc, [headerMap[`${c.colNo}`].toUpperCase()]: c.value }), {}))
            .map((roll, idx) => {
              return rollPredefinedFields.reduce(
                (acc, field) => ({
                  ...acc,
                  [field]: roll[lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field).toUpperCase()]
                }),
                { rowNo: idx + 2 }
              );
            })
            .map(({ supplierRollNo, length, width, ...roll }) => ({ ...roll, rollNo: supplierRollNo, supplierLength: length, supplierWidth: width }))
            .filter(roll => !isEmpty(roll.rollNo));

          if (isEmpty(rollList)) {
            dispatch(showNotification("Roll Upload Failed. Check if Roll Upload Excel Template is Proper.", { type: "danger" }));
          } else {
            let errors = rollList.flatMap((roll, idx) => {
              let errList = [];
              if (!isEmpty(roll.blanketQty) && !REGEX_POSITIVE_DECIMAL.test(roll.blanketQty)) {
                errList.push({
                  column: lodashGet(i18nMessages, `resources.rolls.fields.blanketQty`, "blanketQty").toUpperCase(),
                  row: roll.rowNo,
                  message: "Blanket Qty must be positive number"
                });
              }

              return errList;
            });

            if (!isEmpty(errors)) {
              setErrorList(errors);
            } else {
              const options = {
                url: `invoices/${invoice?.id}/fabric-items/${fabricItem?.id}/rolls/blanket-issue`,
                method: "put",
                data: rollList
              };
              mutate(options, {
                onSuccess: () => {
                  dispatch(showNotification("Blanket Qty updated successfully"));
                  onRefresh && onRefresh();
                  onDrawerClose();
                },
                onFailure: err => {
                  if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
                    setErrorList(err.errors);
                  }
                }
              });
            }
          }
        }
      }
    });
  };

  return (
    <div>
      <ExcelErrorDialog maxWidth="xl" open={errorList?.length > 0 || false} errors={errorList} onClose={() => setErrorList(null)} />

      {/** Drawer For Blanket Qty update for Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">{fabricItem?.rollCount > 0 ? "" : "Upload"} Blanket Issue</Typography>
          </div>
        </div>
        <Divider />
        <SimpleForm style={{ margin: "1em" }} onSubmit={onUpload} footer={<FormFooter />}>
          <FileInput
            source="file"
            label="Blanket Issue File"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            xs={12}
            fullWidth
            validate={[required()]}>
            <FileField source="src" title="title" />
          </FileInput>
        </SimpleForm>
      </Drawer>
    </div>
  );
};

export default BlanketIssueDrawer;
